import React, { useMemo, useEffect, useState } from 'react';
import handlebars from 'handlebars';
import { useSelector, useDispatch } from 'react-redux';
import {
    IFontWeight,
    handlebars as handlebarsUtils,
    ITemplate,
    ITemplateData,
    IWebsite,
    IGetWebsiteSiteNoticeResponse,
    IGetWebsitePrivacyPolicyResponse
} from '@dogado/webcard-shared';

import IFrame from './IFrame';
import { useConfig, useIsomorphicLayoutEffect, useStaticUrl } from '../lib/hooks';
import { sleep, offset, sanitizeHtml, deepMerge } from '../lib/utils';
import { useRouter } from 'next/router';
import { IAction, IStore, IStoreLastFlyoutInteraction, IStoreTemplateData } from '../types';
import { Dispatch } from 'redux';

const fontWeightMap: Record<IFontWeight, number> = {
    thin: 100,
    extralight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900
};

const contentMap: Record<
    string,
    {
        title: string;
        flyout: string;
        subFlyout?: string;
    }
> = {
    title: {
        title: 'Titel',
        flyout: 'content',
        subFlyout: 'profile'
    },
    description: {
        title: 'Beschreibung',
        flyout: 'content',
        subFlyout: 'description'
    },
    tags: {
        title: 'Tags',
        flyout: 'content',
        subFlyout: 'tags'
    },
    openingHours: {
        title: 'Öffnungszeiten',
        flyout: 'content',
        subFlyout: 'openingHours'
    },
    socialMedia: {
        title: 'Soziale Medien',
        flyout: 'content',
        subFlyout: 'socialMedia'
    },
    directions: {
        title: 'Anfahrt',
        flyout: 'content',
        subFlyout: 'directions'
    },
    contact: {
        title: 'Kontakt',
        flyout: 'content',
        subFlyout: 'contact'
    },
    gallery: {
        title: 'Galerie',
        flyout: 'content',
        subFlyout: 'gallery'
    },
    logo: {
        title: 'Logo',
        flyout: 'media'
    },
    teaser: {
        title: 'Titelbild',
        flyout: 'media'
    },
    siteNotice: {
        title: 'Impressum',
        flyout: 'content',
        subFlyout: 'siteNotice'
    },
    privacyPolicy: {
        title: 'Datenschutzerklärung',
        flyout: 'content',
        subFlyout: 'privacyPolicy'
    }
};

function generateFontFace(font: string, fontWeights: IFontWeight[], url: string): string {
    return `<style>
${fontWeights
    .map(
        (weight) => `@font-face {
  font-family: "noto_sans_jp_content";
  font-weight: ${fontWeightMap[weight]};
  font-style: ${/italic$/.test(weight) ? 'italic' : 'normal'};
  src: url("${url}/fonts/noto_sans_jp_content/noto_sans_jp-${weight}.woff2")
    format("woff2"),
    url("${url}/fonts/noto_sans_jp_content/noto_sans_jp-${weight}.woff")
      format("woff");
}`
    )
    .join('\n')}
@font-face {
  font-family: "${font}";
  font-weight: 400;
  font-style: normal;
  src: url("${url}/fonts/${font}/${font}-variable.woff2")
    format("woff2"),
    url("${url}/fonts/${font}/${font}-variable.woff")
      format("woff");
}
</style>`;
}

function createEditableStyle(contentWindow: Window) {
    let primaryColor = document.documentElement.style.getPropertyValue('--primary');
    let primary25Color = document.documentElement.style.getPropertyValue('--primary-25');
    let primaryDarkColor = document.documentElement.style.getPropertyValue('--primary-dark');
    let primaryContrastColor = document.documentElement.style.getPropertyValue('--primary-contrast');

    let css = `
    .editable {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        background: lime;
        opacity: 0;
        border: 1px solid ${primaryColor};
        background-color: ${primary25Color};
        cursor: pointer;
        z-index: 999;
    }

    .editable:hover {
        opacity: 1;
    }

    .editable button {
        border-radius: 4px;
        background-color: ${primaryColor};
        padding: 0 12px;
        height: 34px;
        font-size: 14px;
        color: ${primaryContrastColor};
        font-family: sans-serif;
        transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
        transition-duration: 150ms;
        transition-property: all;
        outline: none !important;
    }

    .editable button:hover {
        background-color: ${primaryDarkColor};
    }

    .editable:before {
        content: attr(data-title);
        position: absolute;
        padding: 4px 8px;
        background: ${primaryColor};
        color: ${primaryContrastColor};
        font-weight: 500;
        font-size: 12px;
        line-height: 1em;
        font-family: sans-serif;
        left: 0;
    }

    .editable[data-direction="top"]:before {
        top: -25px;
    }

    .editable[data-direction="bottom"]:before {
        bottom: -25px;
    }

    .pulse {
        opacity: 1;
        animation: pulse 500ms infinite;
    }

    @keyframes pulse {
        0% {
            transform: scale(1);
        }

        50% {
            transform: scale(1.02);
        }

        100% {
            transform: scale(1);
        }
    }
    `;

    let head = contentWindow.document.head;
    let style = contentWindow.document.createElement('style');

    head.appendChild(style);

    style.type = 'text/css';
    style.appendChild(contentWindow.document.createTextNode(css));
}

function createEditable(contentWindow: Window, el: Element) {
    let key = el.getAttribute('data-editable');

    if (null === key) {
        return;
    }

    let title = contentMap[key].title;

    let editableContainer = contentWindow.document.createElement('div');
    editableContainer.setAttribute('class', 'editable');
    editableContainer.setAttribute('data-title', title);
    editableContainer.setAttribute('data-key', key);

    let editableContainerBtn = contentWindow.document.createElement('button');
    editableContainerBtn.textContent = 'Bearbeiten';
    editableContainer.appendChild(editableContainerBtn);

    contentWindow.document.body.appendChild(editableContainer);

    let calculate = () => {
        if (false === contentWindow.document.contains(el)) {
            contentWindow.document.removeEventListener('DOMContentLoaded', calculate);
            contentWindow.removeEventListener('resize', calculate);
            contentWindow.document.removeEventListener('mouseenter', calculate);
            return;
        }

        let { top, left, width, height } = offset(el, contentWindow);
        let minWidth = 120;
        let minHeight = 80;

        if (width < minWidth) {
            left -= (minWidth - width) / 2;
            width = minWidth;
        }

        if (height < minHeight) {
            top -= (minHeight - height) / 2;
            height = minHeight;
        }

        top -= 10;
        left -= 10;
        width += 20;
        height += 20;

        if (top < 0) {
            top = 0;
        }

        if (left < 0) {
            left = 0;
        }

        editableContainer.setAttribute('data-direction', top > 60 ? 'top' : 'bottom');
        editableContainer.style.top = `${top}px`;
        editableContainer.style.left = `${left}px`;
        editableContainer.style.width = `${width}px`;
        editableContainer.style.height = `${height}px`;
    };

    calculate();

    // Sometimes it takes longer to load and render the style. Therefore a further calculation is delayed.
    contentWindow.document.addEventListener('DOMContentLoaded', calculate);

    // The size of the preview may change. As a result, the container sits differently, so that a recalculation has to take place.
    contentWindow.addEventListener('resize', calculate);

    contentWindow.document.body.addEventListener('mouseenter', calculate);

    return {
        element: editableContainer,
        recalculate: calculate
    };
}

type PreviewProps = {
    editable?: boolean;
    baseHref?: string;
    page?: 'homepage' | 'siteNotice' | 'privacyPolicy';
};

function Preview({ editable = false, baseHref, page }: PreviewProps) {
    let template = useSelector<IStore, ITemplate | undefined>((state) => state.template);
    let [iframeRef, setIframeRef] = useState<HTMLIFrameElement>();
    let editableContainers = useMemo(() => new Map(), []);

    let dispatch = useDispatch<Dispatch<IAction>>();

    let config = useConfig();
    let state = useSelector<IStore, IStore>((state) => state);
    let website = useSelector<IStore, IWebsite | undefined>((state) => state.website);
    let siteNotice = useSelector<IStore, Partial<IGetWebsiteSiteNoticeResponse> | undefined>((state) => state.siteNotice);
    let privacyPolicy = useSelector<IStore, Partial<IGetWebsitePrivacyPolicyResponse> | undefined>((state) => state.privacyPolicy);
    let router = useRouter();
    let templateDataFromStore = useSelector<IStore, IStoreTemplateData>((state) => state.templateData);
    let websiteFont = website && website.font;
    let staticUrl = useStaticUrl();

    let templateData: ITemplateData = deepMerge<ITemplateData>(
        {
            ...state,
            website: website,
            siteNotice,
            privacyPolicy,
            scriptUrl: staticUrl && template ? `${staticUrl}/assets/${template.scriptPath}` : '',
            styleUrl: staticUrl && template ? `${staticUrl}/assets/${template.stylePath}` : '',
            baseUrl: staticUrl ?? '',
            fontFamily: staticUrl && websiteFont && template ? generateFontFace(websiteFont, template.fontWeights || [], staticUrl) : '',
            page: {
                homepage: true
            },
            socialMedia: config.socialMedia,
            product: config.product,
            urls: config.urls
        },
        templateDataFromStore
    );

    if (page) {
        switch (page) {
            case 'siteNotice':
                templateData.page = {
                    siteNotice: true
                };
                break;

            case 'privacyPolicy':
                templateData.page = {
                    privacyPolicy: true
                };
                break;
        }
    }

    if (templateData.website) {
        if (!templateData.website.logoAlt || !templateData.website.logoAlt.trim()) {
            if (templateData.website.customerData?.company) {
                templateData.website.logoAlt = templateData.website.customerData.company;
            } else if (templateData.website.customerData?.firstname && website && website.customerData?.lastname) {
                templateData.website.logoAlt = `${templateData.website.customerData.firstname} ${templateData.website.customerData.lastname}`;
            }
        }
    }

    if (templateData.siteNotice?.content) {
        // Should already have happened, but safe is safe.
        templateData.siteNotice = {
            ...templateData.siteNotice,
            content: sanitizeHtml(templateData.siteNotice.content)
        };
    }

    if (templateData.privacyPolicy?.content) {
        // Should already have happened, but safe is safe.
        templateData.privacyPolicy = {
            ...templateData.privacyPolicy,
            content: sanitizeHtml(templateData.privacyPolicy.content)
        };
    }

    let lastFlyoutInteraction = useSelector<IStore, IStoreLastFlyoutInteraction | undefined>((state) => state.lastFlyoutInteraction);
    let activeSubFlyout = useSelector<IStore, string | undefined>((state) => state.activeSubFlyout);

    switch (activeSubFlyout) {
        case 'siteNotice':
            if (templateData.siteNotice?.content) {
                templateData.page = {
                    siteNotice: true
                };
            }
            break;

        case 'privacyPolicy':
            if (templateData.privacyPolicy?.content) {
                templateData.page = {
                    privacyPolicy: true
                };
            }
            break;
    }

    let render = useMemo(() => {
        return handlebars.compile((template && template.content) || '');
    }, [template]);

    useEffect(() => {
        if (iframeRef && lastFlyoutInteraction) {
            (async () => {
                let { flyout, subFlyout } = lastFlyoutInteraction;

                if (flyout === 'content' && subFlyout && ['siteNotice', 'privacyPolicy'].includes(subFlyout)) {
                    iframeRef.contentWindow?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                    return;
                }

                let getEditableAreas = (): HTMLDivElement[] =>
                    Object.keys(contentMap)
                        .map((key) => {
                            let value = contentMap[key];

                            if (value.flyout === flyout && value.subFlyout === (subFlyout || undefined)) {
                                return (
                                    iframeRef &&
                                    iframeRef.contentWindow?.document.querySelector<HTMLDivElement>(`.editable[data-key="${key}"]`)
                                );
                            }

                            return null;
                        })
                        .filter((v) => v) as HTMLDivElement[];

                let editableAreas = await new Promise<HTMLDivElement[]>(async (resolve) => {
                    let result: HTMLDivElement[];
                    let i = 0;

                    do {
                        i++;

                        result = getEditableAreas();
                    } while (result.length === 0 && i < 5 && (await sleep(200)));

                    resolve(result);
                });

                if (editableAreas.length > 0) {
                    let editableArea = editableAreas[0];
                    let recalculate = editableContainers.get(editableArea);

                    if (recalculate) {
                        recalculate();
                    }

                    let isHover = editableAreas.some((element) => element.matches(':hover'));

                    if (false === isHover) {
                        editableArea.classList.add('pulse');

                        let contentWindow = iframeRef.contentWindow;
                        let top = contentWindow ? offset(editableArea, contentWindow).top - 100 : 0;

                        if (top < 0) {
                            top = 0;
                        }

                        iframeRef.contentWindow?.scrollTo({ top, left: 0, behavior: 'smooth' });

                        setTimeout(() => {
                            editableArea.classList.remove('pulse');
                        }, 1500);
                    }
                }
            })();
        }
    }, [lastFlyoutInteraction]);

    let html = useMemo(() => {
        return render(templateData, {
            helpers: handlebarsUtils.helpers
        });
    }, [templateData, render]);

    useIsomorphicLayoutEffect(() => {
        if (iframeRef && html) {
            let children = iframeRef?.contentWindow?.document?.body?.children;
            let hasChildren = children && children.length > 0;

            if (hasChildren) {
                let body = iframeRef.contentWindow?.document?.querySelector('body');

                if (body) {
                    let bodyContent = html.match(/<body[^>]*>(.*)<\/body>/ms);

                    if (bodyContent) {
                        body.innerHTML = bodyContent[1];
                    }
                }
            } else {
                let doc = iframeRef.contentWindow?.document.open();

                if (doc) {
                    doc.write(html);
                    doc.close();
                }

                let contentWindow = iframeRef.contentWindow;

                if (contentWindow) {
                    createEditableStyle(contentWindow);
                }
            }

            editableContainers.clear();

            if (editable) {
                let editableEls = iframeRef.contentWindow?.document.querySelectorAll('[data-editable]');

                if (editableEls) {
                    Array.from(editableEls).forEach((el) => {
                        let contentWindow = iframeRef && iframeRef.contentWindow;

                        if (contentWindow) {
                            let editable = createEditable(contentWindow, el);

                            if (editable) {
                                let { element, recalculate } = editable;

                                editableContainers.set(element, recalculate);

                                element.addEventListener('click', () => {
                                    let targetPath = el.getAttribute('data-editable');

                                    if (targetPath) {
                                        let { flyout, subFlyout } = contentMap[targetPath] || {};

                                        if (!flyout) {
                                            return;
                                        }

                                        dispatch({
                                            type: 'OPEN_FLYOUT',
                                            flyout,
                                            subFlyout
                                        });

                                        if (targetPath === 'logo') {
                                            dispatch({
                                                type: 'TOGGLE_LOGO_MODAL_OPEN'
                                            });
                                        }

                                        if (targetPath === 'teaser') {
                                            dispatch({
                                                type: 'TOGGLE_TEASER_MODAL_OPEN'
                                            });
                                        }
                                    }
                                });
                            }
                        }
                    });
                }
            }

            if (baseHref) {
                let links = iframeRef.contentWindow?.document.querySelectorAll('a[href^="/"]');

                if (links) {
                    Array.from(links).forEach((link) => {
                        link.addEventListener('click', (e) => {
                            router.push(`${baseHref}${link.getAttribute('href')?.replace('.html', '')}`);

                            e.preventDefault();
                        });
                    });
                }
            }
        }
    }, [html, iframeRef]);

    if (template?.content === undefined || template?.content?.length === 0) {
        return <></>;
    }

    return <IFrame title="test" ref={(newIframeRef) => setIframeRef(newIframeRef || undefined)} />;
}

export default Preview;
